// Tennis Scoring (115) -- live point entry + scoreboard. Singles only.

import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type { TennisScoreResponse } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { OfflineScoreBar } from "@/components/offline/offline-score-bar";
import { ChevronLeft, Undo2, Trophy, Loader2 } from "lucide-react";

export default function TennisMatchScorePage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<TennisScoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    api.getTennisScore(id)
      .then(setData)
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => load(), [load]);

  async function point(side: "home" | "away") {
    if (!id) return;
    setBusy(true);
    try { setData(await api.recordTennisPoint(id, side)); }
    catch (e) {
      if (e instanceof api.QueuedOfflineError) return; // saved offline; bar shows it
      setError(e instanceof api.ApiError ? e.message : "Failed");
    }
    finally { setBusy(false); }
  }
  async function undo() {
    if (!id || !data || data.events.length === 0) return;
    const last = data.events[data.events.length - 1];
    setBusy(true);
    try { setData(await api.undoTennisPoint(id, last.id)); }
    catch (e) { if (!(e instanceof api.QueuedOfflineError)) setError(e instanceof api.ApiError ? e.message : "Failed"); }
    finally { setBusy(false); }
  }
  async function finalize() {
    if (!id || !confirm("Finalise this match?")) return;
    setBusy(true);
    try { setData(await api.finalizeTennis(id)); }
    catch (e) { if (!(e instanceof api.QueuedOfflineError)) setError(e instanceof api.ApiError ? e.message : "Failed"); }
    finally { setBusy(false); }
  }

  if (loading) return <div className="h-40 animate-pulse rounded-lg bg-muted" data-page="tennis-score" data-state="loading" />;
  if (error || !data) return <p className="text-sm text-destructive" data-page="tennis-score" data-error>{error ?? "Not found"}</p>;

  const r = data.result;
  const canRecord = r._actions.can_record;

  function Row({ side }: { side: "home" | "away" }) {
    const t = side === "home" ? r.home : r.away;
    const sets = side === "home" ? r.home_sets_won : r.away_sets_won;
    const games = side === "home" ? r.home_games : r.away_games;
    const won = r.winner_side === side;
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-3" data-side={side}>
        <span className={`min-w-0 truncate text-sm ${won ? "font-semibold" : "font-medium"}`}>{t.name ?? "TBD"}</span>
        <span className="flex items-center gap-4 tabular-nums">
          <span className="w-6 text-center text-lg font-semibold" data-field="sets">{sets}</span>
          <span className="w-6 text-center text-muted-foreground" data-field="games">{games}</span>
          <span className="w-12 text-center text-base font-medium" data-field="game">{r.game_label[side]}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-4" data-page="tennis-score" data-state="ready">
      <Link to={`/events/${r.event_id}`} className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Back to event
      </Link>
      <h1 className="text-xl font-semibold">{r.match_name}</h1>

      <OfflineScoreBar matchId={id} />

      <div className="rounded-lg border border-border bg-background" data-region="scoreboard">
        <div className="flex justify-end gap-4 px-4 pt-2 text-[10px] uppercase text-muted-foreground tabular-nums">
          <span className="w-6 text-center">Sets</span><span className="w-6 text-center">Gms</span><span className="w-12 text-center">Pts</span>
        </div>
        <Row side="home" />
        <div className="border-t border-border/60" />
        <Row side="away" />
        {r.tiebreak_active && <p className="px-4 pb-2 text-xs font-medium text-amber-700">Tiebreak</p>}
        {r.final && <p className="px-4 pb-2 text-sm font-semibold text-emerald-700">Match over - {(r.winner_side === "home" ? r.home.name : r.away.name) ?? "winner"} wins</p>}
      </div>

      {canRecord && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => point("home")} disabled={busy} className="h-16 text-base" data-action="point-home">
              Point: {r.home.name ?? "Home"}
            </Button>
            <Button onClick={() => point("away")} disabled={busy} className="h-16 text-base" data-action="point-away">
              Point: {r.away.name ?? "Away"}
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={undo} disabled={busy || data.events.length === 0} data-action="undo-point">
              <Undo2 className="mr-1 h-4 w-4" /> Undo
            </Button>
            <Button variant="outline" size="sm" onClick={finalize} disabled={busy} data-action="finalize">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trophy className="mr-1 h-4 w-4" />} Finalise
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
